import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Autocomplete, Box } from "@mui/material";
import { ColumnDef } from "@tanstack/react-table";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import FormActionButtons from "pages/QuotationV2/Form/Components/FormActionButtons";
import { autocompleteDropdownStyles } from "Constants/Style";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import {
  fetchSettings,
  setCurrentBankOrLut,
  updateSettings,
} from "slices/Masters/settings/thunk";
import { createSelector } from "reselect";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsTableContainer from "./SettingsTableContainer";

interface BankDetailEntry {
  id: number;
  is_current: boolean;
  bank_name: string;
  account_holder_name: string;
  account_number: string;
  ifsc_code: string;
  branch_name: string;
  updated_at?: string;
  updated_by?: string;
  updated_by_name?: string;
}

interface LutNumberEntry {
  id: number;
  is_current: boolean;
  lut_number: string;
  financial_year: string;
  effective_date: string;
  updated_at?: string;
  updated_by?: string;
  updated_by_name?: string;
}

const selectSettingsState = createSelector(
  (state: any) => state.Settings,
  (settings) => ({
    settings: settings?.settings ?? null,
    loading: settings?.loading ?? false,
    error: settings?.error ?? null,
  })
);

const SettingsPage: React.FC = () => {
  document.title = "Settings";
  const dispatch = useDispatch<any>();
  const { settings, loading, error } = useSelector(selectSettingsState);
  const { canUpdate } = usePermission(
    app_modules?.user_management?.app_name,
    app_modules?.user_management?.modules?.settings
  );
  const [togglingId, setTogglingId] = useState<{
    type: "bank" | "lut";
    id: number;
  } | null>(null);
  const [showEditBankModal, setShowEditBankModal] = useState(false);
  const [editingBank, setEditingBank] = useState<BankDetailEntry | null>(null);
  const [editBankForm, setEditBankForm] = useState({
    bank_name: "",
    account_holder_name: "",
    account_number: "",
    confirm_account_number: "",
    ifsc_code: "",
    branch_name: "",
  });
  const [showEditLutModal, setShowEditLutModal] = useState(false);
  const [editingLut, setEditingLut] = useState<LutNumberEntry | null>(null);
  const [editLutForm, setEditLutForm] = useState({
    lut_number: "",
    financial_year: "",
    effective_date: "",
  });
  const [showAddLutModal, setShowAddLutModal] = useState(false);
  const [addLutForm, setAddLutForm] = useState({
    lut_number: "",
    financial_year: "",
    effective_date: "",
  });

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  const settingsRow = settings?.data ?? settings ?? null;
  const bankDetails: BankDetailEntry[] = Array.isArray(settingsRow?.bank_details)
    ? (settingsRow.bank_details as BankDetailEntry[])
    : [];
  const lutNumbers: LutNumberEntry[] = Array.isArray(settingsRow?.lut_number)
    ? (settingsRow.lut_number as LutNumberEntry[])
    : [];

  const handleSetDefaultBank = async (id: number) => {
    setTogglingId({ type: "bank", id });
    try {
      await dispatch(
        setCurrentBankOrLut({ type: "bank", id })
      ).unwrap();
      toast.success("Default bank detail updated.");
      dispatch(fetchSettings());
    } catch (err: any) {
      toast.error(err?.message || err?.error || "Failed to set default bank.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleSetDefaultLut = async (id: number) => {
    setTogglingId({ type: "lut", id });
    try {
      await dispatch(
        setCurrentBankOrLut({ type: "lut", id })
      ).unwrap();
      toast.success("Default LUT number updated.");
      dispatch(fetchSettings());
    } catch (err: any) {
      toast.error(err?.message || err?.error || "Failed to set default LUT.");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteBank = async (entry: BankDetailEntry) => {
    if (!window.confirm("Delete this bank detail?")) return;
    const updated = bankDetails.filter((b) => b.id !== entry.id);
    if (updated.length === 0 && bankDetails.some((b) => b.is_current)) {
      toast.error("Cannot delete the only bank detail.");
      return;
    }
    try {
      await dispatch(
        updateSettings({ payload: { bank_details: updated } })
      ).unwrap();
      toast.success("Bank detail removed.");
      dispatch(fetchSettings());
    } catch (err: any) {
      toast.error(err?.message || err?.error || "Failed to delete.");
    }
  };

  const openEditBank = (row: BankDetailEntry) => {
    setEditingBank(row);
    const acc = row.account_number || "";
    setEditBankForm({
      bank_name: row.bank_name || "",
      account_holder_name: row.account_holder_name || "",
      account_number: acc,
      confirm_account_number: acc,
      ifsc_code: row.ifsc_code || "",
      branch_name: row.branch_name || "",
    });
    setShowEditBankModal(true);
  };

  const handleSaveEditBank = async () => {
    if (!editingBank) return;
    const { bank_name, account_holder_name, account_number, confirm_account_number, ifsc_code, branch_name } = editBankForm;
    if (!bank_name?.trim() || !account_holder_name?.trim() || !account_number?.trim() || !ifsc_code?.trim() || !branch_name?.trim()) {
      toast.error("All fields are required.");
      return;
    }
    if (account_number.trim() !== confirm_account_number?.trim()) {
      toast.error("Account Number and Confirm Account Number do not match.");
      return;
    }
    const updatedList = bankDetails.map((b) =>
      b.id === editingBank.id
        ? {
            ...b,
            bank_name: bank_name.trim(),
            account_holder_name: account_holder_name.trim(),
            account_number: account_number.trim(),
            ifsc_code: ifsc_code.trim(),
            branch_name: branch_name.trim(),
          }
        : b
    );
    try {
      await dispatch(
        updateSettings({ payload: { bank_details: updatedList } })
      ).unwrap();
      toast.success("Bank detail updated.");
      setShowEditBankModal(false);
      setEditingBank(null);
      dispatch(fetchSettings());
    } catch (err: any) {
      toast.error(err?.message || err?.error || "Failed to update.");
    }
  };

  const openEditLut = (row: LutNumberEntry) => {
    setEditingLut(row);
    setEditLutForm({
      lut_number: row.lut_number || "",
      financial_year: row.financial_year || "",
      effective_date: row.effective_date ? moment(row.effective_date).format("YYYY-MM-DD") : "",
    });
    setShowEditLutModal(true);
  };

  const handleSaveEditLut = async () => {
    if (!editingLut) return;
    const { lut_number, financial_year, effective_date } = editLutForm;
    if (!lut_number?.trim()) {
      toast.error("LUT number is required.");
      return;
    }
    const updatedList = lutNumbers.map((l) =>
      l.id === editingLut.id
        ? {
            ...l,
            lut_number: lut_number?.trim() ?? "",
            financial_year: financial_year?.trim() ?? "",
            effective_date: effective_date?.trim() ?? "",
          }
        : l
    );
    try {
      await dispatch(
        updateSettings({ payload: { lut_number: updatedList } })
      ).unwrap();
      toast.success("LUT number updated.");
      setShowEditLutModal(false);
      setEditingLut(null);
      dispatch(fetchSettings());
    } catch (err: any) {
      toast.error(err?.message || err?.error || "Failed to update.");
    }
  };

  const openAddLut = () => {
    setAddLutForm({ lut_number: "", financial_year: "", effective_date: "" });
    setShowAddLutModal(true);
  };

  const handleAddLut = async () => {
    const { lut_number, financial_year, effective_date } = addLutForm;
    if (!lut_number?.trim()) {
      toast.error("LUT number is required.");
      return;
    }
    const newId = lutNumbers.length > 0 ? Math.max(...lutNumbers.map((l) => l.id)) + 1 : 1;
    const newEntry = {
      id: newId,
      is_current: lutNumbers.length === 0,
      lut_number: lut_number.trim(),
      financial_year: financial_year?.trim() ?? "",
      effective_date: effective_date?.trim() ?? "",
    };
    try {
      await dispatch(
        updateSettings({ payload: { lut_number: [...lutNumbers, newEntry] } })
      ).unwrap();
      toast.success("LUT number added.");
      setShowAddLutModal(false);
      dispatch(fetchSettings());
    } catch (err: any) {
      toast.error(err?.message || err?.error || "Failed to add LUT number.");
    }
  };

  const bankColumns = useMemo<ColumnDef<BankDetailEntry, any>[]>(
    () => [
      {
        id: "default",
        header: "Default",
        size: 120,
        cell: ({ row }) => (
          console.log("row", row),
          <div className="d-flex align-items-center gap-2">
            <Form.Check
              type="radio"
              name="default-bank"
              checked={row.original.is_current}
              disabled={
                togglingId?.type === "bank" && togglingId?.id === row.original.id
              }
              onChange={() => handleSetDefaultBank(row.original.id)}
            />
            {row.original.is_current && (
              <span className="badge bg-success-subtle text-success">Default</span>
            )}
          </div>
        ),
      },
      { id: "bank_name", header: "Bank Name", accessorFn: (r) => r.bank_name || "-" },
      { id: "account_holder", header: "Account Holder", accessorFn: (r) => r.account_holder_name || "-" },
      { id: "account_number", header: "Account No.", accessorFn: (r) => r.account_number || "-" },
      { id: "ifsc_code", header: "IFSC Code", accessorFn: (r) => r.ifsc_code || "-" },
      { id: "branch", header: "Branch", accessorFn: (r) => r.branch_name || "-" },

      {
        id: "updated_by",
        header: "Updated By/Date",
        cell: ({ row }) => <NameWithDate name={row.original.updated_by_name ?? row.original.updated_by} date={row.original.updated_at} />,
      },
      {
        id: "action",
        header: () => <span className="text-center w-100 d-block">Action</span>,
        size: 100,
        cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-center gap-1">
            {canUpdate ? (
              <>
                <button
                  type="button"
                 className="btn btn-sm btn-soft-primary p-1"
                  title="Edit"
                  onClick={() => openEditBank(row.original)}

                >
                  <EditIcon fontSize="small" color="primary"/>
                </button>
              </>
            ) : (
              "-"
            )}
          </div>
        ),
      },
    ],
    [togglingId, canUpdate]
  );

  const lutColumns = useMemo<ColumnDef<LutNumberEntry, any>[]>(
    () => [
      {
        id: "default",
        header: "Default",
        size: 120,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <Form.Check
              type="radio"
              name="default-lut"
              checked={row.original.is_current}
              disabled={
                togglingId?.type === "lut" && togglingId?.id === row.original.id
              }
              onChange={() => handleSetDefaultLut(row.original.id)}
            />
            {row.original.is_current && (
              <span className="badge bg-success-subtle text-success">Default</span>
            )}
          </div>
        ),
      },
      { id: "lut_number", header: "LUT Number", accessorFn: (r) => r.lut_number || "-" },
      { id: "financial_year", header: "Financial Year", accessorFn: (r) => r.financial_year || "-" },
      {
        id: "effective_date",
        header: "Effective Date",
        accessorFn: (r) =>
          r.effective_date ? moment(r.effective_date).format("DD-MMM-YYYY") : "-",
      },
      {
        id: "updated_by",
        header: "Updated By/Date",
        cell: ({ row }) => <NameWithDate name={row.original.updated_by_name ?? row.original.updated_by} date={row.original.updated_at} />,
      },
      {
        id: "action",
        header: () => <span className="text-center w-100 d-block">Action</span>,
        size: 100,
        cell: ({ row }) => (
          <div className="d-flex align-items-center justify-content-center">
            {canUpdate ? (
              <button
                type="button"
                className="btn btn-sm btn-soft-primary p-1"
                title="Edit"
                onClick={() => openEditLut(row.original)}
              >
                <EditIcon fontSize="small" color="primary"/>
              </button>
            ) : (
              "-"
            )}
          </div>
        ),
      },
    ],
    [togglingId, canUpdate]
  );

  const financialYearOptions = useMemo(() => {
    return Array.from({ length: 10 }, (_, i) => {
      const y = new Date().getFullYear() - 5 + i;
      return `${y}-${String((y + 1) % 100).padStart(2, "0")}`;
    });
  }, []);

  return (
    <ListingPageContainer>
      <Row id="settingsList">
        <Col lg={12}>
          {/* Bank Details Table */}
          <Card className="shadow-none mb-4">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Bank Details</h6>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <SettingsTableContainer
                  columns={bankColumns}
                  data={bankDetails}
                  isBordered
                  emptyMessage="No bank details found."
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>

          {/* LUT Numbers Table */}
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">LUT Numbers</h6>
                </Col>
           
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <SettingsTableContainer
                  columns={lutColumns}
                  data={lutNumbers}
                  isBordered
                  emptyMessage="No LUT numbers found."
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEditBankModal} onHide={() => { setShowEditBankModal(false); setEditingBank(null); }} centered className="modal-lg">
        <Modal.Header closeButton>
          <Modal.Title>Update Bank Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label>Bank Name</Form.Label>
            <Form.Control
              value={editBankForm.bank_name}
              onChange={(e) => setEditBankForm((p) => ({ ...p, bank_name: e.target.value }))}
              placeholder="Bank name"
              className="rounded"
            />
          </Form.Group>
          <Form.Group className="mb-4">
            <Form.Label>Account Holder Name</Form.Label>
            <Form.Control
              value={editBankForm.account_holder_name}
              onChange={(e) => setEditBankForm((p) => ({ ...p, account_holder_name: e.target.value }))}
              placeholder="Account holder name"
              className="rounded"
            />
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  value={editBankForm.account_number}
                  onChange={(e) => setEditBankForm((p) => ({ ...p, account_number: e.target.value }))}
                  placeholder="Account number"
                  className="rounded"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label>Confirm Account Number</Form.Label>
                <Form.Control
                  value={editBankForm.confirm_account_number}
                  onChange={(e) => setEditBankForm((p) => ({ ...p, confirm_account_number: e.target.value }))}
                  placeholder="Confirm account number"
                  className="rounded"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label>IFSC Code</Form.Label>
                <Form.Control
                  value={editBankForm.ifsc_code}
                  onChange={(e) => setEditBankForm((p) => ({ ...p, ifsc_code: e.target.value }))}
                  placeholder="IFSC code"
                  className="rounded"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-4">
                <Form.Label>Branch Name</Form.Label>
                <Form.Control
                  value={editBankForm.branch_name}
                  onChange={(e) => setEditBankForm((p) => ({ ...p, branch_name: e.target.value }))}
                  placeholder="Branch name"
                  className="rounded"
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="mb-0 mt-2" style={{ marginLeft: "calc(-1 * var(--bs-modal-padding, 1rem))", marginRight: "calc(-1 * var(--bs-modal-padding, 1rem))" }}>
            <hr className="mb-0 w-100" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <FormActionButtons
            showSaveDraft={false}
            saveButtonLabel="Update"
            wrapperClassName="d-flex flex-wrap gap-2 justify-content-center w-100"
            onCancel={() => { setShowEditBankModal(false); setEditingBank(null); }}
            onSave={handleSaveEditBank}
          />
        </Modal.Footer>
      </Modal>

      <Modal show={showEditLutModal} onHide={() => { setShowEditLutModal(false); setEditingLut(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit LUT Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>LUT Number</Form.Label>
            <Form.Control
              value={editLutForm.lut_number}
              onChange={(e) => setEditLutForm((p) => ({ ...p, lut_number: e.target.value }))}
              placeholder="Enter LUT Number"
              className="rounded"
            />
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Financial Year</Form.Label>
                <Autocomplete
                  options={financialYearOptions}
                  getOptionLabel={(option) => option || ""}
                  isOptionEqualToValue={(option, value) => option === value}
                  value={editLutForm.financial_year || null}
                  onChange={(_, newValue) =>
                    setEditLutForm((p) => ({ ...p, financial_year: newValue ?? "" }))
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps} style={{ padding: 0 }}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.8,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            cursor: "pointer",
                          }}
                        >
                          <span>{option}</span>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Year"
                      size="small"
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={editLutForm.effective_date}
                  onChange={(e) => setEditLutForm((p) => ({ ...p, effective_date: e.target.value }))}
                  className="rounded"
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="mb-0 mt-2" style={{ marginLeft: "calc(-1 * var(--bs-modal-padding, 1rem))", marginRight: "calc(-1 * var(--bs-modal-padding, 1rem))" }}>
            <hr className="mb-0 w-100" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <FormActionButtons
            showSaveDraft={false}
            saveButtonLabel="Update"
            wrapperClassName="d-flex flex-wrap gap-2 justify-content-center w-100"
            onCancel={() => { setShowEditLutModal(false); setEditingLut(null); }}
            onSave={handleSaveEditLut}
          />
        </Modal.Footer>
      </Modal>

      {/* <Modal show={showAddLutModal} onHide={() => setShowAddLutModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add LUT Number</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>LUT Number</Form.Label>
            <Form.Control
              value={addLutForm.lut_number}
              onChange={(e) => setAddLutForm((p) => ({ ...p, lut_number: e.target.value }))}
              placeholder="Enter LUT Number"
              className="rounded"
            />
          </Form.Group>
          <Row className="g-2">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Financial Year</Form.Label>
                <Autocomplete
                  options={financialYearOptions}
                  getOptionLabel={(option) => option || ""}
                  isOptionEqualToValue={(option, value) => option === value}
                  value={addLutForm.financial_year || null}
                  onChange={(_, newValue) =>
                    setAddLutForm((p) => ({ ...p, financial_year: newValue ?? "" }))
                  }
                  ListboxProps={autocompleteDropdownStyles.ListboxProps}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps} style={{ padding: 0 }}>
                        <Box
                          sx={{
                            px: 1.5,
                            py: 0.8,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            cursor: "pointer",
                          }}
                        >
                          <span>{option}</span>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <BootstrapTextField
                      {...params}
                      placeholder="Select Year"
                      size="small"
                      sx={autocompleteDropdownStyles.inputSx}
                    />
                  )}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Effective Date</Form.Label>
                <Form.Control
                  type="date"
                  value={addLutForm.effective_date}
                  onChange={(e) => setAddLutForm((p) => ({ ...p, effective_date: e.target.value }))}
                  placeholder="DD / MM / YYYY"
                  className="rounded"
                />
              </Form.Group>
            </Col>
          </Row>
          <div className="mb-0 mt-2" style={{ marginLeft: "calc(-1 * var(--bs-modal-padding, 1rem))", marginRight: "calc(-1 * var(--bs-modal-padding, 1rem))" }}>
            <hr className="mb-0 w-100" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <FormActionButtons
            showSaveDraft={false}
            saveButtonLabel="Update"
            wrapperClassName="d-flex flex-wrap gap-2 justify-content-end w-100"
            onCancel={() => setShowAddLutModal(false)}
            onSave={handleAddLut}
          />
        </Modal.Footer>
      </Modal> */}
    </ListingPageContainer>
  );
};

export default SettingsPage;
